import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import OnOffToggle from './OnOffToggle';
import { Label } from './ui/label';
import ActionButton from './ui/action-button';
import { Star, ExternalLink, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import DashboardPageLayout from './DashboardPageLayout';
import FormSection from './FormSection';

const ReviewsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [reviewLinkEnabled, setReviewLinkEnabled] = useState(false);
  const [reviewLinkUrl, setReviewLinkUrl] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const orgId = localStorage.getItem('organizationId');
      const orgResponse = await api.get(`/organizations/${orgId}`);
      const orgData = orgResponse.data;
      
      setReviewLinkEnabled(orgData.reviewLinkEnabled || false);
      setReviewLinkUrl(orgData.reviewLinkUrl || '');
      
    } catch (error) {
      console.error('Error fetching review settings:', error);
      toast.error('Failed to load review settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (reviewLinkEnabled && !reviewLinkUrl) {
      toast.error('Please enter a review link URL');
      return;
    }

    // Validate URL format
    if (reviewLinkEnabled && reviewLinkUrl) {
      try {
        new URL(reviewLinkUrl);
      } catch {
        toast.error('Please enter a valid URL (e.g., https://g.page/your-business/review)');
        return;
      }
    }

    setSaving(true);
    try {
      const orgId = localStorage.getItem('organizationId');
      await api.put(`/organizations/${orgId}`, {
        reviewLinkEnabled: reviewLinkEnabled,
        reviewLinkUrl: reviewLinkUrl
      });

      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <DashboardPageLayout
      title="Customer Reviews"
      subtitle="Configure review links to collect customer feedback"
      actions={
        <ActionButton
          onClick={handleSave}
          loading={saving}
          icon={CheckCircle}
          className="bg-orange-600 hover:bg-orange-700"
        >
          Save Settings
        </ActionButton>
      }
    >
      <div className="space-y-6">
        {/* STEP 1: Review Link Configuration */}
        <FormSection
          stepNumber={1}
          title="Review Link Configuration"
          description="Add a review button to customer tickets"
          color="#F97316"
        >
          <div className="space-y-4">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md border border-gray-200">
              <div>
                <Label className="text-sm sm:text-base font-semibold">Enable Review Button</Label>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Show "Leave a Review" button on customer tickets
                </p>
              </div>
              <OnOffToggle
                value={reviewLinkEnabled}
                onChange={setReviewLinkEnabled}
              />
            </div>

            {/* Review Link URL */}
            {reviewLinkEnabled && (
              <div>
                <Label className="text-base font-semibold mb-2 block">
                  Review Link URL *
                </Label>
                <Input
                  value={reviewLinkUrl}
                  onChange={(e) => setReviewLinkUrl(e.target.value)}
                  placeholder="https://g.page/your-business/review"
                  className="text-base"
                />
                <p className="text-base text-gray-500 mt-2">
                  📍 <strong>Popular platforms:</strong> Google Reviews, Yelp, TripAdvisor, Facebook Reviews
                </p>
                <p className="text-base text-gray-500 mt-1">
                  💡 <strong>Tip:</strong> Get your Google review link from Google Business Profile → Share review form
                </p>
              </div>
            )}
          </div>
        </FormSection>

        {/* STEP 2: Preview */}
        {reviewLinkEnabled && reviewLinkUrl && (
          <FormSection
            stepNumber={2}
            title="Preview"
            description="See how the review button looks on customer tickets"
            color="#EAB308"
          >
            <div className="bg-gray-50 border border-gray-200 rounded-md p-6">
              <p className="text-base text-gray-600 mb-4 text-center">
                This is how customers will see the review button on their ticket:
              </p>
              
              {/* Preview Button */}
              <a
                href={reviewLinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-3 rounded-md font-semibold flex items-center justify-center gap-2 shadow-lg transition-all block"
              >
                <Star className="w-5 h-5 fill-white" />
                Leave a Review
              </a>
              
              <p className="text-base text-gray-500 mt-4 text-center">
                Click to test the link (opens in new tab)
              </p>
            </div>
          </FormSection>
        )}

        {/* STEP 3: How It Works */}
        <FormSection
          stepNumber={3}
          title="How It Works"
          description="Customer journey with review links"
          color="#10B981"
        >
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-base text-blue-900 mb-2">
              <strong>Customer Flow:</strong> Booking → Ticket → Review Button → Your Review Page → 5-Star Review ⭐
            </p>
            <p className="text-base text-blue-700">
              Review button appears right after booking when customers are most satisfied.
            </p>
          </div>
        </FormSection>
      </div>
    </DashboardPageLayout>
  );
};

export default ReviewsTab;
